import React, { forwardRef } from 'react';
import { Controller } from 'react-hook-form';
import { RadioButton as GuiRadioButton } from '@oliasoft-open-source/react-ui-library';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';

const ForwardedGuiRadioButton = forwardRef((props, ref) => {
  return <GuiRadioButton {...props} inputRef={ref} />;
});
ForwardedGuiRadioButton.displayName = 'ForwardedGuiRadioButton';

/**
 * A functional component that renders a group of radio buttons using the `Controller` from React Hook Form.
 * This component is customizable with various props for flexibility in rendering and behavior.
 *
 * @typedef {Object} RawRadioButtonProps
 * @property {string} name - The name of the input field, used to register the input with React Hook Form.
 * @property {Object} control - The control object provided by React Hook Form for managing form state.
 * @property {boolean} [disabled=false] - Determines whether the radio button group should be disabled.
 * @property {function} [onChange=null] - Custom callback function to be invoked when the value changes.
 * @property {boolean} [inline=false] - Specifies whether the radio buttons should be displayed inline.
 * @property {Array} [options=[]] - Array of options to render as radio buttons, with each option representing a value and label.
 *
 * @param {RawRadioButtonProps} props - The props for the RawRadioButton component.
 * @returns {JSX.Element} A set of radio buttons rendered using the `Controller` and wrapped with additional behavior.
 */
const RawRadioButton = ({ name, control, disabled = false, onChange = null, inline = false, options = [] }) => (
  <Controller
    name={name}
    control={control}
    render={({ field }) => {
      const { onChange: fieldOnChange, ...props } = field;
      return (
        <ForwardedGuiRadioButton
          {...props}
          disabled={disabled}
          inline={inline}
          options={options}
          onChange={(e) => {
            fieldOnChange(e);
            if (onChange) {
              onChange(e);
            }
          }}
        />
      );
    }}
  />
);

export const RadioButton = withErrorBoundary(RawRadioButton);
